import type { GameState, Pile } from '../../engine/game-state.js';
import { createGrid, CLASSIC_LAYOUT } from '../../render/classic/layout.js';
import type { ClassicSpec } from '../../render/classic/types.js';
import { CARD_W, CARD_H } from '../../render/classic/types.js';
import { FOUNDATION_IDS, TABLEAU_IDS, yukonRules } from './rules.js';

// 7-column grid. Top row: foundations on the right (no stock/waste).
// Bottom row: 7 fan-down tableau columns.
const COLS = 7;
const TOP_Y = 24;
const TABLEAU_Y = TOP_Y + CARD_H + CLASSIC_LAYOUT.ROW_GAP;
const FELT_W = COLS * CARD_W + (COLS - 1) * CLASSIC_LAYOUT.COL_GAP + 48;
// Worst-case tableau column: a long unsorted pile, ~19 cards fanned.
const FELT_H = TABLEAU_Y + CARD_H + 19 * CLASSIC_LAYOUT.FAN_DOWN + 24;

const layout = createGrid({
  cols: COLS,
  feltWidth: FELT_W,
  rows: [
    { y: TOP_Y, slots: [null, null, null, ...FOUNDATION_IDS] },
    { y: TABLEAU_Y, fanDown: true, slots: [...TABLEAU_IDS] },
  ],
});

function dragStackIds(state: GameState, cardId: string): string[] {
  for (const pile of Object.values(state.piles)) {
    const idx = pile.cards.findIndex((c) => c.id === cardId);
    if (idx === -1) continue;
    const head = pile.cards[idx]!;
    if (!head.faceUp) return [];
    if (pile.type !== 'tableau') {
      if (idx !== pile.cards.length - 1) return [];
      return [cardId];
    }
    // Yukon: grab any face-up card and everything piled on it.
    return pile.cards.slice(idx).map((c) => c.id);
  }
  return [];
}

const AUTO_SEND_ORDER = [...FOUNDATION_IDS, ...TABLEAU_IDS];

// Single-click parity with classic Klondike: try to auto-send the top face-up
// card (foundations first, then tableau). Group moves still require a drag.
function tryAutoSendInPlace(state: GameState, fromPile: Pile, cardId: string): boolean {
  const top = fromPile.cards[fromPile.cards.length - 1];
  if (!top || top.id !== cardId || !top.faceUp) return false;
  for (const toId of AUTO_SEND_ORDER) {
    if (toId === fromPile.id) continue;
    const toPile = state.piles[toId];
    if (!toPile) continue;
    const move = { from: fromPile.id, to: toId, cardId };
    if (!yukonRules.canMove(state, move)) continue;
    const idx = fromPile.cards.findIndex((c) => c.id === cardId);
    const moving = fromPile.cards.splice(idx);
    toPile.cards.push(...moving);
    state.movesCount++;
    yukonRules.afterMove?.(state, move);
    return true;
  }
  return false;
}

export const yukonClassicSpec: ClassicSpec = {
  rules: yukonRules,
  feltWidth: FELT_W,
  feltHeight: FELT_H,
  slotForPile: (id) => layout.slot(id),
  dropExclude: () => [],
  dragStackIds,
  autoSendOrder: () => AUTO_SEND_ORDER,
  onPileClick(state, pile, cardId) {
    return tryAutoSendInPlace(state, pile, cardId);
  },
  statusText(state) {
    if (yukonRules.isLost!(state)) return 'No moves left';
    return null;
  },
};
